/**
 * Z-index layers for the safe-zones surface.
 *
 * **Project constraint.** The host project's modal stack is bunched at
 * six-figure values with single-digit gaps:
 *   1000005  Modal chrome / backdrop
 *   1000007  Modal main content
 *   1000008  Toolbar dropdowns / menus
 *   1000010  `<Modal>`-mounted sub-modals (Replace Captions, Reframe, …)
 *
 * Safe-zones has to slot **between** dropdowns and sub-modals — a 1-unit
 * gap. So thousand-step layering inside safe-zones isn't possible today
 * without first widening the project's stack to thousand-step gaps. When
 * that happens, swap `LAYER_STEP` to `1000` and the offsets here keep
 * working (just bump them to thousand-aligned values).
 *
 * **Convention.** Add a new safe-zone layer here rather than inlining
 * z-index numerics anywhere else in `src/safe-zones` or
 * `src/components/stage/safe-zones`.
 */

/** Anchored to the project's existing modal layer. */
const PROJECT_MODAL_BASE = 1000000;

/**
 * Distance between safe-zone layers. `1` today (forced by the project's
 * single-digit gaps); switch to `1000` once the project's modal stack is
 * refactored to widen those gaps and rescale the offsets below.
 */
const LAYER_STEP = 1;

export const SAFE_ZONE_Z_INDEX = {
  /**
   * The portaled overlay root. Sits above the prewarmer / canvas layer
   * (`1000006`) and at the same slot as the modal main content (`1000007`)
   * — DOM order resolves the tie so the portal (mounted to `document.body`
   * after the modal commits) paints above the iframe and Mux player
   * inside the modal. Crucially **below** the project's toolbar dropdowns
   * (`1000008`: AR / Edit Mode / Publish and Share) so those continue to
   * layer cleanly over the preview chrome.
   *
   * Today: `1000000 + 7 × 1 = 1000007`.
   * If `LAYER_STEP` becomes `1000`, this scales to `1000000 + 7 × 1000`.
   */
  overlay: PROJECT_MODAL_BASE + 7 * LAYER_STEP,

  /**
   * The Safe Zones picker dropdown panel. Slotted at the same level as the
   * project's other toolbar dropdowns (AR, Edit Mode / Published Version,
   * Publish and Share) so they all paint on top of the overlay consistently.
   * DOM order breaks ties between dropdowns; the picker's portal commits
   * after its trigger so the panel paints above the overlay correctly.
   *
   * Today: `1000000 + 8 × 1 = 1000008`.
   */
  dropdownPanel: PROJECT_MODAL_BASE + 8 * LAYER_STEP,

  /**
   * Tooltips spawned from safe-zone toolbar controls (e.g. the "i" hints
   * button). One notch above the picker panel and overlay so the tooltip
   * never gets covered by either; still below `<Modal>`-mounted sub-modals
   * at `1000010` so opening Replace Captions / Reframe stacks above any
   * lingering tooltip.
   *
   * Today: `1000000 + 9 × 1 = 1000009`.
   */
  toolbarTooltip: PROJECT_MODAL_BASE + 9 * LAYER_STEP,
} as const;
